
import React, { useState } from 'react';
import { Building2, Users, ArrowRight, Mail, Globe, ChevronDown, Briefcase, Phone, ShieldCheck, MapPin, Home } from 'lucide-react';
import PolicyModal from './PolicyModal';
import Logo from './Logo';
import { COUNTRIES_CURRENCIES, BUSINESS_CATEGORIES } from '../constants';
import { useLanguage } from '../contexts/LanguageContext';
import { supabase } from '../lib/supabase';

interface SignupPageProps {
  onSignup: (role: 'business' | 'individual', name: string, identifier: string, country: string, businessType?: string) => void;
  onNavigateToLogin: () => void;
  onBackToHome: () => void;
}

const SignupPage: React.FC<SignupPageProps> = ({ onSignup, onNavigateToLogin, onBackToHome }) => {
  const [activeTab, setActiveTab] = useState<'business' | 'individual'>('business');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [country, setCountry] = useState('Saudi Arabia'); 
  const [businessType, setBusinessType] = useState(BUSINESS_CATEGORIES[0]);
  const [isLoading, setIsLoading] = useState(false);
  const { t, language } = useLanguage();
  const [agreed, setAgreed] = useState(false);
  const [policyType, setPolicyType] = useState<'privacy' | 'terms' | null>(null);

  const selectedCountryData = COUNTRIES_CURRENCIES.find(c => c.country === country) || COUNTRIES_CURRENCIES[0];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!agreed) return;
    setIsLoading(true);

    try {
        const fullPhone = `${selectedCountryData.dialCode}${phone.replace(/\s+/g, '')}`;
        const { data, error } = await supabase.auth.signUp({
            email: email,
            password: password,
            options: {
                data: {
                    name,
                    role: activeTab,
                    country,
                    phone: fullPhone,
                    businessCategory: activeTab === 'business' ? businessType : undefined,
                    createdAt: new Date().toISOString(),
                    isPaid: false
                }
            }
        });

        if (error) {
            alert(error.message);
        } else if (data.user) {
            onSignup(activeTab, name, email, country, activeTab === 'business' ? businessType : undefined);
        }
    } catch (err) {
        console.error("Signup error", err);
    } finally {
        setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-wedding-50 flex flex-col items-center justify-center p-4 relative overflow-hidden">
      <PolicyModal isOpen={!!policyType} type={policyType} onClose={() => setPolicyType(null)} />
      
      <div className="w-full max-w-lg bg-white rounded-[2.5rem] shadow-2xl z-10 overflow-hidden border border-white/50 backdrop-blur-sm">
        <div className="p-8 pb-4 text-center">
          <div className="flex justify-between items-start mb-2">
            <button onClick={onBackToHome} className="text-gray-400 hover:text-wedding-600 transition-colors flex items-center text-[10px] font-black uppercase tracking-widest">
              <Home className="w-3.5 h-3.5 mr-1" />
              {t('button.backHome')}
            </button>
          </div>
          <div className="flex justify-center mb-6">
             <button 
                onClick={onBackToHome}
                className="p-4 bg-wedding-50 rounded-2xl shadow-inner cursor-pointer hover:rotate-6 transition-transform"
             >
                <Logo className="w-12 h-12 text-wedding-600" />
             </button>
          </div>
          <h1 className="text-3xl font-serif font-black tracking-tight text-gray-900 mb-1">
            {activeTab === 'business' ? t('signup.title.business') : t('signup.title.individual')}
          </h1>
          <p className="text-gray-400 text-[10px] font-black uppercase tracking-[0.2em]">{t('app.name')} • Secure Registration</p>
        </div>

        <div className="px-8 mb-6">
          <div className="flex bg-gray-50 p-1.5 rounded-2xl border border-gray-100">
            <button onClick={() => setActiveTab('business')} className={`flex-1 flex items-center justify-center py-3 text-[10px] font-black uppercase tracking-widest rounded-xl transition-all ${activeTab === 'business' ? 'bg-white text-wedding-600 shadow-md ring-1 ring-black/5' : 'text-gray-400 hover:text-gray-600'}`}>
              <Building2 className="w-4 h-4 mr-2" />{t('login.business')}
            </button>
            <button onClick={() => setActiveTab('individual')} className={`flex-1 flex items-center justify-center py-3 text-[10px] font-black uppercase tracking-widest rounded-xl transition-all ${activeTab === 'individual' ? 'bg-white text-wedding-600 shadow-md ring-1 ring-black/5' : 'text-gray-400 hover:text-gray-600'}`}>
              <Users className="w-4 h-4 mr-2" />{t('login.individual')}
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-10 pt-0 space-y-6 max-h-[65vh] overflow-y-auto custom-scrollbar">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 ml-1">{t('signup.country')}</label>
              <div className="relative group">
                 <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-wedding-500">
                    <Globe className="w-4 h-4" />
                 </div>
                 <select value={country} onChange={(e) => setCountry(e.target.value)} className="w-full pl-12 pr-4 py-4 bg-gray-50 border border-gray-100 rounded-2xl outline-none appearance-none font-bold text-sm">
                   {COUNTRIES_CURRENCIES.map(c => <option key={c.country} value={c.country}>{c.flag} {c.country}</option>)}
                 </select>
                 <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
              </div>
            </div>

            <div className="md:col-span-2">
              <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 ml-1">{activeTab === 'business' ? t('signup.businessName') : t('signup.fullName')}</label>
              <div className="relative group">
                 <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-wedding-500">
                    <Building2 className="w-4 h-4" />
                 </div>
                 <input type="text" required value={name} onChange={(e) => setName(e.target.value)} className="w-full pl-12 pr-4 py-4 bg-gray-50 border border-gray-100 rounded-2xl outline-none text-sm font-medium" placeholder={activeTab === 'business' ? "Grand Palace Riyadh" : "Sami Abdullah"} />
              </div>
            </div>

            <div className="md:col-span-2">
              <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 ml-1">Mobile Number</label>
              <div className="flex gap-3">
                <div className="w-28 px-4 py-4 bg-gray-900 border border-gray-800 rounded-2xl text-xs font-black text-white flex items-center justify-center shadow-lg">
                   {selectedCountryData.dialCode}
                </div>
                <div className="flex-1 relative group">
                   <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-wedding-500">
                      <Phone className="w-4 h-4" />
                   </div>
                   <input type="tel" required value={phone} onChange={(e) => setPhone(e.target.value)} className="w-full pl-12 pr-4 py-4 bg-gray-50 border border-gray-100 rounded-2xl outline-none text-sm font-bold tracking-widest" placeholder="50 123 4567" />
                </div>
              </div>
            </div>

            <div className="md:col-span-2">
              <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 ml-1">{t('login.email')}</label>
              <div className="relative group">
                 <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-wedding-500">
                    <Mail className="w-4 h-4" />
                 </div>
                 <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="w-full pl-12 pr-4 py-4 bg-gray-50 border border-gray-100 rounded-2xl outline-none text-sm" placeholder="contact@example.com" />
              </div>
            </div>
            
            <div className="md:col-span-2">
              <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 ml-1">{t('login.password')}</label>
              <div className="relative group">
                 <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-wedding-500">
                    <ShieldCheck className="w-4 h-4" />
                 </div>
                 <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="w-full pl-12 pr-4 py-4 bg-gray-50 border border-gray-100 rounded-2xl outline-none text-sm" placeholder="••••••••" />
              </div>
            </div>
          </div>
          
          <div className="flex items-start pt-2 px-1">
            <input id="terms" type="checkbox" required checked={agreed} onChange={(e) => setAgreed(e.target.checked)} className="mt-1 w-5 h-5 text-wedding-600 border-gray-300 rounded-lg focus:ring-wedding-500 transition-all cursor-pointer" />
            <label htmlFor="terms" className="ml-4 text-[10px] leading-relaxed text-gray-500 font-bold uppercase tracking-tight"> {t('signup.agree')} <button type="button" onClick={() => setPolicyType('terms')} className="text-wedding-600 hover:underline">{t('signup.terms')}</button> {t('signup.and')} <button type="button" onClick={() => setPolicyType('privacy')} className="text-wedding-600 hover:underline">{t('signup.privacy')}</button> </label>
          </div>

          <button type="submit" disabled={isLoading || !agreed} className="w-full bg-wedding-600 hover:bg-wedding-700 text-white font-black uppercase tracking-widest py-5 rounded-[1.5rem] shadow-2xl shadow-wedding-200 transition-all flex items-center justify-center space-x-2 disabled:opacity-50">
            {isLoading ? <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <>{t('signup.create')} <ArrowRight className="w-5 h-5" /></>}
          </button>
          
          <div className="text-center pt-2">
             <button type="button" onClick={onNavigateToLogin} className="text-[10px] font-black text-gray-400 uppercase tracking-widest hover:text-wedding-600 transition-colors">
                {t('signup.alreadyAccount')} <span className="text-wedding-600 underline">Sign In</span>
             </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SignupPage;
